import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Share2, Pin, Trash2, Globe, Lock, Shield, Link as LinkIcon, Pencil, Plus, Copy, Check, X as XIcon } from "lucide-react";
import WidgetRenderer from "@/components/WidgetRenderer";
import PublishDialog from "@/components/PublishDialog";
import WidgetEditor from "@/components/WidgetEditor";
import { useAuth } from "@/context/AuthContext";

export default function DashboardView() {
  const { id, slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [columns, setColumns] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [showPublish, setShowPublish] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editing, setEditing] = useState(null); // widget config or {new:true}

  const load = async () => {
    try {
      const { data } = await api.get(`/dashboards/${id}`);
      setDash(data);
      try { const { data: ds } = await api.get(`/datasets/${data.dataset_id}`); setColumns(ds.columns || []); } catch {}
      try { const { data: dl } = await api.get("/datasets"); setDatasets(dl.datasets || []); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      nav(`/t/${slug}/dashboards`);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (updates) => {
    try { await api.patch(`/dashboards/${id}`, updates); await load(); }
    catch { toast.error("Failed"); }
  };

  const togglePin = () => patch({ pinned: !dash.pinned });
  const del = async () => {
    if (!window.confirm(`Delete "${dash.title}"?`)) return;
    try { await api.delete(`/dashboards/${id}`); toast.success("Deleted"); nav(`/t/${slug}/dashboards`); }
    catch { toast.error("Failed"); }
  };
  const copyPublic = () => {
    if (!dash.public_token) return;
    navigator.clipboard.writeText(`${window.location.origin}/public/d/${dash.public_token}`);
    toast.success("Public link copied");
  };

  const currentWidgets = () => (dash.rendered_widgets || []).map(w => w.config);

  const saveWidget = async (cfg) => {
    const list = currentWidgets();
    const idx = list.findIndex(w => w.id === cfg.id);
    if (idx >= 0) list[idx] = cfg;
    else list.push({ ...cfg, id: cfg.id || undefined });
    await patch({ widgets: list });
    toast.success("Widget saved");
    setEditing(null);
  };
  const deleteWidget = async (wid) => {
    const list = currentWidgets().filter(w => w.id !== wid);
    await patch({ widgets: list });
    toast.success("Widget removed");
    setEditing(null);
  };
  const duplicateWidget = async (wid) => {
    const list = currentWidgets();
    const found = list.find(w => w.id === wid);
    if (!found) return;
    const copy = { ...found, id: undefined, title: `${found.title || "Widget"} (copy)` };
    list.push(copy);
    await patch({ widgets: list });
    toast.success("Duplicated");
  };

  if (!dash) return <div className="p-10 text-slate-400">Loading…</div>;
  const isOwner = dash.viewer_is_owner || user?.role === "company_admin" || user?.role === "platform_admin";
  const widgets = dash.rendered_widgets || [];

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10">
      <Link to={`/t/${slug}/dashboards`} className="link-quiet inline-flex items-center gap-2 text-sm mb-6" data-testid="back-dashboards">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {(dash.datasets_used || []).map(d => <span key={d.id} className="chip">{d.name}</span>)}
            {dash.pinned && <span className="chip chip-warning"><Pin size={10} /> Pinned</span>}
            {dash.is_public && <span className="chip chip-success"><Globe size={10} /> Public</span>}
            {!dash.is_public && dash.published && <span className="chip chip-primary"><Globe size={10} /> Published</span>}
            {!dash.published && <span className="chip"><Lock size={10} /> Draft</span>}
            {dash.access_control?.enabled && <span className="chip chip-primary"><Shield size={10} /> Access · {dash.access_control.column}</span>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900" data-testid="dashboard-title">{dash.title}</h1>
          {dash.description && <p className="text-slate-500 mt-2 max-w-2xl">{dash.description}</p>}
          {dash.access_scope && <div className="mt-3 chip chip-primary">Viewing filtered to: {dash.access_scope.column} = {String(dash.access_scope.value)}</div>}
        </div>
        {isOwner && (
          <div className="flex flex-wrap gap-2">
            {editMode ? (
              <button onClick={() => setEditMode(false)} data-testid="exit-edit" className="btn-primary"><Check size={14} /> Done editing</button>
            ) : (
              <button onClick={() => setEditMode(true)} data-testid="enter-edit" className="btn-ghost"><Pencil size={14} /> Edit dashboard</button>
            )}
            {dash.is_public && dash.public_token && <button onClick={copyPublic} data-testid="copy-public" className="btn-ghost"><LinkIcon size={14} /> Copy link</button>}
            <button onClick={togglePin} data-testid="btn-pin" className="btn-ghost"><Pin size={14} /> {dash.pinned ? "Unpin" : "Pin"}</button>
            <button onClick={() => setShowPublish(true)} data-testid="btn-publish" className="btn-primary"><Share2 size={14} /> Publish</button>
            <button onClick={del} data-testid="btn-delete" className="btn-ghost hover:!text-[var(--danger)]"><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      {editMode && (
        <div className="mb-6 flex items-center justify-between bg-blue-50/70 border border-blue-100 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Pencil size={14} className="text-[var(--primary)]" />
            <span className="font-medium">Edit mode</span>
            <span className="text-slate-500">— hover any widget to edit, duplicate, or delete it.</span>
          </div>
          <button onClick={() => setEditing({ new: true })} data-testid="add-widget" className="btn-primary"><Plus size={14} /> Add widget</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {widgets.length === 0 ? (
          <div className="col-span-full card text-slate-500 text-center py-12">
            No widgets yet. {isOwner && <button onClick={() => { setEditMode(true); setEditing({ new: true }); }} className="text-[var(--primary)] font-medium hover:underline">Add your first widget</button>}
          </div>
        ) : widgets.map((w, i) => {
          const isBig = w.config?.type === "table";
          return (
            <div key={w.id || i} className={`relative group ${isBig ? "md:col-span-2 xl:col-span-3" : ""}`} data-testid={`widget-${i}`}>
              <WidgetRenderer widget={w} height={240} />
              {editMode && isOwner && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-lg border border-[var(--border)] shadow-sm p-1">
                  <button onClick={() => setEditing({ ...w.config, id: w.id, dataset_id: w.dataset_id })} data-testid={`edit-w-${w.id}`} title="Edit" className="p-1.5 hover:bg-slate-100 rounded"><Pencil size={12} /></button>
                  <button onClick={() => duplicateWidget(w.id)} data-testid={`dup-w-${w.id}`} title="Duplicate" className="p-1.5 hover:bg-slate-100 rounded"><Copy size={12} /></button>
                  <button onClick={() => { if (window.confirm("Delete this widget?")) deleteWidget(w.id); }} data-testid={`del-w-${w.id}`} title="Delete" className="p-1.5 hover:bg-red-50 hover:text-[var(--danger)] rounded"><Trash2 size={12} /></button>
                </div>
              )}
              {w.dataset_name && (dash.datasets_used || []).length > 1 && (
                <div className="absolute bottom-2 left-2 chip text-[9px] bg-white/90 backdrop-blur">{w.dataset_name}</div>
              )}
            </div>
          );
        })}
      </div>

      {showPublish && <PublishDialog dashboard={dash} columns={columns} onClose={() => setShowPublish(false)} onSaved={load} />}
      {editing && (
        <WidgetEditor
          widget={editing.new ? null : editing}
          datasets={datasets.length ? datasets : (dash.datasets_used || [])}
          defaultDatasetId={editing.dataset_id || dash.dataset_id}
          onClose={() => setEditing(null)}
          onSave={saveWidget}
          onDelete={editing.new ? null : () => deleteWidget(editing.id)}
        />
      )}
    </div>
  );
}
